import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Checkbox,
  LinearProgress, Chip, TextField, CircularProgress,
  Autocomplete, Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Science as ScienceIcon,
  AutoAwesome as SparkleIcon,
  CopyAll as CopyAllIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import { colors } from '../../../themes/typography';
import { fetchCandidateSuggestion } from '../../ExperimentManagement/services/experimentManagementApi';

const BatchExperimentDialog = ({
  open,
  onClose,
  slots,
  experimentType,
  machineData,
  clientId,
  filters,
  filterOptions,
  aiSuggestions,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [checkedSlots, setCheckedSlots] = React.useState(new Set());
  const [discount, setDiscount] = React.useState(15);
  const [dateFrom, setDateFrom] = React.useState(() => dayjs().add(1, 'month').startOf('month'));
  const [dateTo, setDateTo] = React.useState(() => dayjs().add(1, 'month').startOf('month').add(8, 'week'));
  const [creating, setCreating] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [results, setResults] = React.useState(null); // { success: [], failed: [] }

  // Per-slot candidate selection for replacement experiments
  const [perSlotCandidates, setPerSlotCandidates] = React.useState({});
  const [perSlotAiCandidates, setPerSlotAiCandidates] = React.useState({}); // all AI candidates per slot
  const [suggestingAll, setSuggestingAll] = React.useState(false);

  // Per-slot discount for price experiments
  const [perSlotDiscounts, setPerSlotDiscounts] = React.useState({});

  // All products for manual candidate selection (replacement experiments)
  const allProductsCache = React.useRef(null);
  const [allProducts, setAllProducts] = React.useState([]);
  const [allProductsLoading, setAllProductsLoading] = React.useState(false);

  const isReplacement = experimentType === 'reemplazo_producto';
  const isDiscount = experimentType === 'promo_precio_unitario';

  // Fetch all products when dialog opens for replacement type
  React.useEffect(() => {
    if (open && isReplacement && clientId) {
      if (allProductsCache.current) {
        setAllProducts(allProductsCache.current);
        return;
      }
      const fetchProducts = async () => {
        setAllProductsLoading(true);
        try {
          const token = localStorage.getItem('access_token');
          const response = await axios.get(
            `${API_BASE_URL}/all-products?clientId=${clientId}`,
            { headers: createAuthHeader(token) }
          );
          const products = response.data.products || [];
          allProductsCache.current = products;
          setAllProducts(products);
        } finally {
          setAllProductsLoading(false);
        }
      };
      fetchProducts();
    }
  }, [open, isReplacement, clientId]);

  // Initialize checked slots and pre-populate candidates from AI suggestions
  React.useEffect(() => {
    if (open && slots.length > 0) {
      setCheckedSlots(new Set(slots.map((_, i) => i)));
      setResults(null);
      setProgress(0);
      setCreating(false);

      // Initialize per-slot discounts with default value
      if (isDiscount) {
        const discounts = {};
        slots.forEach((_, i) => { discounts[i] = discount; });
        setPerSlotDiscounts(discounts);
      }

      // Pre-populate per-slot candidates from AI suggestions
      if (aiSuggestions && Array.isArray(aiSuggestions)) {
        const candidates = {};
        const aiCandidatesMap = {};
        aiSuggestions.forEach(({ slot, suggestion }) => {
          const slotIndex = slots.findIndex(s =>
            s.row === slot.row && s.column === slot.column
          );
          if (slotIndex >= 0 && suggestion?.candidates?.length > 0) {
            aiCandidatesMap[slotIndex] = suggestion.candidates;
            const top = suggestion.candidates[0];
            candidates[slotIndex] = {
              productId: top.productId,
              productName: top.productName,
              improvementPercentage: top.improvementPercentage
            };
          }
        });
        setPerSlotCandidates(candidates);
        setPerSlotAiCandidates(aiCandidatesMap);
      } else {
        setPerSlotCandidates({});
        setPerSlotAiCandidates({});
      }
    }
  }, [open, slots, aiSuggestions]);

  const toggleSlot = (index) => {
    setCheckedSlots(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    if (checkedSlots.size === slots.length) {
      setCheckedSlots(new Set());
    } else {
      setCheckedSlots(new Set(slots.map((_, i) => i)));
    }
  };

  const checkedCount = checkedSlots.size;

  // Suggest All: fetch AI candidates for all checked slots in parallel
  const handleSuggestAll = async () => {
    if (!machineData || checkedCount === 0) return;
    setSuggestingAll(true);
    try {
      const checkedSlotsList = slots.filter((_, i) => checkedSlots.has(i));
      const results = await Promise.allSettled(
        checkedSlotsList.map(slot =>
          fetchCandidateSuggestion(
            clientId,
            machineData.machineId,
            slot.d_product_id,
            'reemplazo_producto'
          ).then(suggestion => ({ slot, suggestion }))
        )
      );

      const newCandidates = { ...perSlotCandidates };
      const newAiCandidates = { ...perSlotAiCandidates };
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          const { suggestion } = r.value;
          const slotIndex = slots.findIndex(s =>
            s.row === checkedSlotsList[idx].row && s.column === checkedSlotsList[idx].column
          );
          if (slotIndex >= 0 && suggestion?.candidates?.length > 0) {
            newAiCandidates[slotIndex] = suggestion.candidates;
            const top = suggestion.candidates[0];
            newCandidates[slotIndex] = {
              productId: top.productId,
              productName: top.productName,
              improvementPercentage: top.improvementPercentage
            };
          }
        }
      });
      setPerSlotCandidates(newCandidates);
      setPerSlotAiCandidates(newAiCandidates);
    } finally {
      setSuggestingAll(false);
    }
  };

  const handleCreate = async () => {
    if (checkedCount === 0 || !machineData) return;

    setCreating(true);
    setProgress(0);
    const success = [];
    const failed = [];
    const token = localStorage.getItem('access_token');

    const slotsToCreate = slots.filter((_, i) => checkedSlots.has(i));

    // Group slots by product + candidate (replacements) or product + discount (discounts)
    // so we make one API call per unique combination.
    const groupMap = new Map();
    for (const slot of slotsToCreate) {
      const slotIndex = slots.indexOf(slot);
      const candidate = perSlotCandidates[slotIndex];
      const candidateId = isReplacement && candidate ? candidate.productId : null;
      const slotDiscount = isDiscount ? (perSlotDiscounts[slotIndex] ?? discount) : null;
      const groupKey = `${slot.d_product_id}_${candidateId || ''}_${slotDiscount ?? ''}`;
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, { slots: [], slotIndices: [], candidate, discount: slotDiscount });
      }
      const group = groupMap.get(groupKey);
      group.slots.push(slot);
      group.slotIndices.push(slotIndex);
    }
    const groups = [...groupMap.values()];

    for (let g = 0; g < groups.length; g++) {
      const group = groups[g];
      const firstSlot = group.slots[0];
      try {
        // Determine status based on dates
        const today = dayjs();
        let status = 'accepted';
        if (dateFrom.isBefore(today, 'day')) {
          status = dateTo.isBefore(today, 'day') ? 'finished' : 'running';
        }

        const body = {
          dStoreId: Number(machineData.machineId),
          dProductId: Number(firstSlot.d_product_id),
          dateFrom: dateFrom.format('YYYY-MM-DD'),
          dateTo: dateTo.format('YYYY-MM-DD'),
          experimentType,
          status,
          tags: [],
          isAi: false,
          innovation: false,
          selectionIds: group.slots.map(s => s.selection_id).filter(Boolean)
        };

        // Add discount for price experiments (per-slot)
        if (isDiscount) {
          body.pricingMode = 'discount';
          body.discount = group.discount;
        }

        // Add candidate for replacement experiments
        if (isReplacement && group.candidate) {
          body.newDProductId = Number(group.candidate.productId);
        }

        const response = await axios.post(
          `${API_BASE_URL}/experiments/create?clientId=${clientId}`,
          body,
          { headers: createAuthHeader(token) }
        );
        success.push(...group.slots.map(slot => ({ ...slot, _experimentResponse: response.data })));
      } catch (err) {
        for (const slot of group.slots) {
          failed.push({ slot, error: err.response?.data?.message || err.message });
        }
      }

      setProgress(Math.round(((g + 1) / groups.length) * 100));
    }

    setResults({ success, failed });
    setCreating(false);

    // If all succeeded, dispatch mutation event and close immediately
    if (failed.length === 0) {
      window.dispatchEvent(new CustomEvent('experimentMutation'));
      onSuccess?.(success);
    }
  };

  const handleClose = () => {
    if (creating) return; // Don't close while creating
    if (results && results.success.length > 0) {
      window.dispatchEvent(new CustomEvent('experimentMutation'));
      onSuccess?.();
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '16px',
        fontWeight: 600
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon sx={{ fontSize: 20 }} />
          {t('machines_batchCreate')} ({checkedCount})
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={creating}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Slot list */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1
          }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: colors.text.secondary }}>
              {t('machines_selectedSlots')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isReplacement && !results && (
                <Button
                  size="small"
                  aria-label={t('machines_suggestAll')}
                  startIcon={suggestingAll ? <CircularProgress size={12} /> : <SparkleIcon sx={{ fontSize: 14 }} />}
                  onClick={handleSuggestAll}
                  disabled={suggestingAll || creating || checkedCount === 0}
                  sx={{
                    textTransform: 'none',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#f59e0b',
                    '&:hover': { backgroundColor: 'rgba(245, 158, 11, 0.08)' }
                  }}
                >
                  {t('machines_suggestAll')}
                </Button>
              )}
              <Button size="small" onClick={toggleAll} sx={{ textTransform: 'none', fontSize: '12px' }}>
                {checkedSlots.size === slots.length ? t('deselectAll') : t('selectAll')}
              </Button>
            </Box>
          </Box>
          <Box sx={{
            maxHeight: 320,
            overflow: 'auto',
            border: `1px solid ${colors.border.light}`,
            borderRadius: '8px'
          }}>
            {slots.map((slot, index) => {
              const result = results?.success.find(s => s.d_product_id === slot.d_product_id);
              const failure = results?.failed.find(f => f.slot.d_product_id === slot.d_product_id);
              const candidate = perSlotCandidates[index];
              const isChecked = checkedSlots.has(index);

              return (
                <Box
                  key={`${slot.row}-${slot.column}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    px: 1.5,
                    py: 0.75,
                    borderBottom: index < slots.length - 1 ? `1px solid ${colors.border.light}` : 'none',
                    backgroundColor: result ? 'rgba(16, 185, 129, 0.05)' : failure ? 'rgba(239, 68, 68, 0.05)' : 'transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={isChecked}
                      onChange={() => toggleSlot(index)}
                      size="small"
                      disabled={creating || results !== null}
                    />
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: colors.text.muted, minWidth: 28 }}>
                      {slot.selection_id || `${slot.row}-${slot.column}`}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: colors.text.primary, flex: 1 }}>
                      {slot.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {slot.productAvgSales > 0 && (
                        <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>
                          {Number(slot.productAvgSales).toFixed(1)}u
                        </Typography>
                      )}
                      {slot.productMargin > 0 && (
                        <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>
                          €{Number(slot.productMargin).toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                    {result && <SuccessIcon sx={{ fontSize: 16, color: colors.status.success }} />}
                    {failure && (
                      <ErrorIcon
                        sx={{ fontSize: 16, color: colors.critical?.text || '#ef4444' }}
                        titleAccess={failure.error}
                      />
                    )}
                  </Box>

                  {/* Per-slot discount input for price experiments */}
                  {isDiscount && !results && isChecked && !creating && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 5, mt: 0.5 }}>
                      <TextField
                        type="number"
                        size="small"
                        value={perSlotDiscounts[index] ?? discount}
                        onChange={(e) => {
                          const val = Math.max(1, Math.min(100, parseInt(e.target.value) || 0));
                          setPerSlotDiscounts(prev => ({ ...prev, [index]: val }));
                        }}
                        inputProps={{ min: 1, max: 100 }}
                        sx={{
                          width: 72,
                          '& .MuiInputBase-root': { fontSize: '12px' },
                          '& .MuiInputBase-input': { py: '4px', textAlign: 'center' }
                        }}
                        InputProps={{
                          endAdornment: <Typography sx={{ fontSize: '11px', color: colors.text.muted, ml: -0.5 }}>%</Typography>
                        }}
                      />
                      {checkedCount > 1 && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CopyAllIcon sx={{ fontSize: '14px !important' }} />}
                          onClick={() => {
                            const val = perSlotDiscounts[index] ?? discount;
                            setPerSlotDiscounts(prev => {
                              const next = { ...prev };
                              slots.forEach((_, i) => {
                                if (checkedSlots.has(i)) { next[i] = val; }
                              });
                              return next;
                            });
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: '11px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            py: 0.25,
                            px: 1,
                            minWidth: 0,
                            borderColor: 'rgba(99,102,241,0.3)',
                            color: '#6366f1',
                            '&:hover': { borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.06)' }
                          }}
                        >
                          {t('machines_applyAll')}
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Per-slot candidate Autocomplete — shows AI candidates + manual search */}
                  {isReplacement && !results && isChecked && !creating && (() => {
                    const slotAiCandidates = (perSlotAiCandidates[index] || []).map(c => ({
                      id: c.productId,
                      name: c.productName,
                      improvementPercentage: c.improvementPercentage,
                      isAi: true
                    }));
                    const selectedValue = candidate
                      ? { id: candidate.productId, name: candidate.productName, improvementPercentage: candidate.improvementPercentage }
                      : null;

                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 5, mt: 0.5 }}>
                        <Autocomplete
                          size="small"
                          value={selectedValue}
                          options={slotAiCandidates.length > 0
                            ? [...slotAiCandidates, ...allProducts.filter(p => !slotAiCandidates.some(ai => ai.id === p.id))]
                            : allProducts
                          }
                          groupBy={(option) => option.isAi ? t('machines_aiCandidates') : t('machines_allProducts')}
                          getOptionLabel={(opt) => opt.name || ''}
                          isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                          loading={allProductsLoading}
                          filterOptions={(options, state) => {
                            if (!state.inputValue || state.inputValue.length < 2) {
                              return options.filter(o => o.isAi);
                            }
                            const filter = state.inputValue.toLowerCase();
                            return options
                              .filter(o => o.isAi || (o.name && o.name.toLowerCase().includes(filter)))
                              .slice(0, 25);
                          }}
                          noOptionsText={t('machines_typeToSearch')}
                          onChange={(_, newValue) => {
                            if (newValue) {
                              setPerSlotCandidates(prev => ({
                                ...prev,
                                [index]: {
                                  productId: newValue.id,
                                  productName: newValue.name,
                                  improvementPercentage: newValue.improvementPercentage ?? null
                                }
                              }));
                            } else {
                              setPerSlotCandidates(prev => {
                                const next = { ...prev };
                                delete next[index];
                                return next;
                              });
                            }
                          }}
                          PaperComponent={({ children, ...paperProps }) => (
                            <Box {...paperProps} sx={{ ...paperProps.sx, backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border.light}`, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                              {children}
                              {slotAiCandidates.length > 0 && (
                                <Typography sx={{ fontSize: '10px', fontStyle: 'italic', color: colors.text.muted, textAlign: 'center', py: 0.5, borderTop: `1px solid ${colors.border.light}` }}>
                                  {t('machines_typeToSearchOther')}
                                </Typography>
                              )}
                            </Box>
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                                {option.isAi && <SparkleIcon sx={{ fontSize: 14, color: '#f59e0b' }} />}
                                <Typography sx={{ fontSize: '12px', flex: 1 }}>{option.name}</Typography>
                                {option.improvementPercentage != null && (
                                  <Typography sx={{
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    color: option.improvementPercentage >= 0 ? colors.status.success : '#ef4444'
                                  }}>
                                    {option.improvementPercentage >= 0 ? '+' : ''}{Math.round(option.improvementPercentage)}%
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={candidate ? '' : t('machines_searchProduct')}
                              size="small"
                            />
                          )}
                          sx={{
                            flex: 1,
                            maxWidth: 340,
                            '& .MuiInputBase-root': { fontSize: '12px', py: 0 },
                            '& .MuiInputBase-input': { py: '4px !important' }
                          }}
                        />
                        {candidate && checkedCount > 1 && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CopyAllIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={() => {
                              setPerSlotCandidates(prev => {
                                const next = { ...prev };
                                slots.forEach((_, i) => {
                                  if (checkedSlots.has(i)) {
                                    next[i] = { ...candidate };
                                  }
                                });
                                return next;
                              });
                            }}
                            sx={{
                              textTransform: 'none',
                              fontSize: '11px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              py: 0.25,
                              px: 1,
                              minWidth: 0,
                              borderColor: 'rgba(99,102,241,0.3)',
                              color: '#6366f1',
                              '&:hover': { borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.06)' }
                            }}
                          >
                            {t('machines_applyAll')}
                          </Button>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Shared parameters */}
        {!results && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={isDiscount ? t('discount') : t('replacement')}
                size="small"
                color={isDiscount ? 'primary' : 'success'}
                sx={{ fontWeight: 500 }}
              />
              {isReplacement && Object.keys(perSlotCandidates).length > 0 && (
                <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>
                  {t('machines_candidatesAssigned', { count: Object.keys(perSlotCandidates).length })}
                </Typography>
              )}
            </Box>

            {/* Discount is now per-slot, shown inline with each slot above */}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', '& > *': { flex: '1 1 140px' } }}>
                <DatePicker
                  label={t('startDate')}
                  value={dateFrom}
                  onChange={(val) => {
                    setDateFrom(val);
                    if (val) {
                      setDateTo(val.add(8, 'week'));
                    }
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label={t('endDate')}
                  value={dateTo}
                  onChange={(val) => setDateTo(val)}
                  minDate={dateFrom}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        )}

        {/* Progress */}
        {creating && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 4, height: 6 }} />
            <Typography sx={{ fontSize: '12px', color: colors.text.muted, mt: 0.5, textAlign: 'center' }}>
              {progress}%
            </Typography>
          </Box>
        )}

        {/* Results summary */}
        {results && (
          <Box sx={{
            mt: 2,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: results.failed.length === 0 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)',
            border: `1px solid ${results.failed.length === 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`
          }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
              {results.success.length > 0 && (
                <>{t('machines_batchSuccess', { count: results.success.length })}</>
              )}
              {results.failed.length > 0 && (
                <Box component="span" sx={{ color: colors.critical?.text || '#ef4444', ml: results.success.length > 0 ? 1 : 0 }}>
                  {t('machines_batchFailed', { count: results.failed.length })}
                </Box>
              )}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={creating} sx={{ textTransform: 'none' }}>
          {results ? t('close') : t('cancel')}
        </Button>
        {!results && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || checkedCount === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {creating
              ? t('machines_creating')
              : t('machines_batchCreateButton', { count: checkedCount })
            }
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(BatchExperimentDialog);
